import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, MapPin, Camera, Users, RefreshCw, Bell, Mic } from "lucide-react";
import { useFarm } from "../services/farmContext";
import { SettingsChangesContext } from "../services/settingsChanges";
import { useAuth } from "../services/authContext";
const allSections = [
  { to: "/settings", label: "Farm profile", icon: Home, end: true },

  { to: "/settings/emails", label: "Emails", icon: Home },
  { to: "/settings/notifications", label: "Notifications", icon: Bell },
  { to: "/settings/badges", label: "Patches", icon: Home },
  { to: "/settings/places", label: "Places", icon: MapPin },
  { to: "/settings/terrain", label: "Terrain", icon: MapPin },
  { to: "/settings/neighbors", label: "Neighbors", icon: MapPin },
  { to: "/settings/photos", label: "Photos", icon: Camera },
  { to: "/settings/moderation", label: "Moderation", icon: Users },
  { to: "/settings/cameras", label: "Cameras", icon: Camera },
  { to: "/settings/videos", label: "Videos", icon: Camera },
  { to: "/settings/users", label: "Users & access", icon: Users },
  { to: "/settings/app", label: "App", icon: RefreshCw },
  { to: "/settings/gps", label: "GPS", icon: MapPin },
  { to: "/settings/live-listening", label: "Live listening", icon: Mic },
];
export default function AdminSettingsLayout({ moderationOnly = false }: { moderationOnly?: boolean }) {
  const { user, privileges } = useAuth();
  const canManageSettings = !!privileges.settings?.view && (user?.roles || [user?.role]).includes("Admin");
  const canModerate = !!(privileges.contributionReview?.view || privileges.photoModeration?.view);
  const sections = allSections.filter(section => section.to === "/settings/moderation" ? canModerate : !moderationOnly || canManageSettings);
  const { activeFarm } = useFarm();
  const [dirty, setDirty] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const click = (event: MouseEvent) => {
      const target =
        event.target instanceof Element ? event.target.closest("a") : null;
      if (
        !dirty ||
        !target ||
        event.defaultPrevented ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey ||
        target.target === "_blank"
      )
        return;
      const destination = new URL(target.href, window.location.href);
      if (
        destination.origin === window.location.origin &&
        destination.pathname !== window.location.pathname &&
        !window.confirm("Discard unsaved settings changes?")
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    const unload = (event: BeforeUnloadEvent) => {
      if (dirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    document.addEventListener("click", click, true);
    window.addEventListener("beforeunload", unload);
    return () => {
      document.removeEventListener("click", click, true);
      window.removeEventListener("beforeunload", unload);
    };
  }, [dirty]);
  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <header>
        <h1>Settings</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage {activeFarm?.name || "your farm"} and shared workspace
          settings.
        </p>
      </header>
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        <label className="lg:hidden">
          <span className="sr-only">Settings section</span>
          <select
            className="w-full border bg-white rounded-xl p-3 text-sm font-medium"
            value={
              sections.find((section) => section.to === location.pathname || (section.to !== "/settings" && location.pathname.startsWith(section.to + "/")))
                ?.to || "/settings"
            }
            onChange={(event) => {
              if (!dirty || window.confirm("Discard unsaved settings changes?"))
                navigate(event.target.value);
            }}
          >
            {sections.map((section) => (
              <option key={section.to} value={section.to}>
                {section.label}
              </option>
            ))}
          </select>
        </label>
        <nav
          aria-label="Settings sections"
          className="hidden lg:flex lg:flex-col gap-1 lg:w-48 lg:shrink-0 lg:self-start lg:sticky lg:top-0"
        >
          {sections.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-3 rounded-xl text-sm font-medium whitespace-nowrap ${isActive ? "bg-usda-100 text-usda-900" : "text-gray-600 hover:bg-gray-100"}`
              }
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="flex-1 min-w-0">
          <SettingsChangesContext.Provider value={setDirty}>
            <Outlet />
          </SettingsChangesContext.Provider>
        </div>
      </div>
    </div>
  );
}
